import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { reactQueryRequest } from 'api/makeQuery'
import { Request as postCrisisStreamAssignProviderRequest } from 'api/requests/v1/crisis/post-crisis-stream-assign-provider'
import { Request as postCrisisStreamAssignProviderPhoneRequest } from 'api/requests/v1/crisis/post-crisis-stream-assign-provider-phone'
import { MEMBER_STATUSES } from 'constants/member'
import { DASHBOARD } from 'constants/routing'
import useVisit from 'hooks/useVisit'
import PendingVisit from 'models/Provider/visits/PendingVisit'
import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { CrisisVisitVerifyPhoneValues } from 'schemas/CrisisVisit'
import Modals from 'singletons/Modals'
import Pusher from 'singletons/Pusher'
import User from 'singletons/User'
import { isCrisisProviderPhoneVerified, isCrisisVisit } from 'types/Crisis/crisis-stream'

export const useCrisisStream = ({
  readOnly = false,
  visitOverride,
}: { readOnly?: boolean; visitOverride?: PendingVisit } = {}) => {
  const visitFromRoute = useVisit()
  const visit = visitOverride || visitFromRoute

  const { provider } = User
  const { pendingVisitId, eventStreamId, status, memberExtendedData: ext } = visit || {}

  const navigate = useNavigate()

  const { providerId, preferredPhone = {} } = provider || {}

  const phoneNumber = preferredPhone?.number

  const isCrisis = !!visit && isCrisisVisit(visit)
  const queryClient = useQueryClient()

  const streamQueryKey = useMemo(() => ['crisis-stream', eventStreamId, status], [eventStreamId, status])

  const crisisStreamQueryEnabled = !!eventStreamId && !readOnly

  const { data: crisisStreamData, isFetching: isCrisisStreamFetching } = useQuery({
    enabled: crisisStreamQueryEnabled,
    queryKey: streamQueryKey,
    queryFn: async () => {
      const data = await reactQueryRequest('getCrisisStream', { streamId: eventStreamId })
      return data
    },
    refetchInterval: process.env.NODE_ENV === 'test' ? false : 1000 * 60,
    refetchIntervalInBackground: process.env.NODE_ENV !== 'test',
    staleTime: process.env.NODE_ENV === 'test' ? 0 : 1000 * 60,
  })

  const { stream } = crisisStreamData || {}

  const { callParticipants } = stream || {}

  // Pusher subscription for real-time crisis call updates
  useEffect(() => {
    if (!eventStreamId || readOnly) return

    // Subscribe to crisis call channel
    const channelName = `private-crisis-calls-${eventStreamId}`
    Pusher.subscribeChannel(channelName)

    const removeListener = Pusher.listen((e) => {
      switch (e?.event) {
        case 'update-crisis-call': {
          // Invalidate crisis stream query to refresh data
          queryClient.invalidateQueries({ queryKey: streamQueryKey })
          break
        }
        default:
          // Ignore other events
          break
      }
    })

    return () => {
      removeListener()
      Pusher.unsubscribeChannel(channelName)
    }
  }, [eventStreamId, readOnly, queryClient, streamQueryKey])

  const claimVisitMutation = useMutation({
    mutationFn: async (data: postCrisisStreamAssignProviderRequest) => {
      return await reactQueryRequest('postCrisisStreamAssignProvider', data)
    },
  })

  const isPhoneVerified = isCrisisProviderPhoneVerified(stream)

  const shouldVerifyPhone = !!isCrisis && !!stream && !isPhoneVerified

  const onCancelVerify = useCallback(() => {
    navigate(DASHBOARD, { replace: true })
  }, [navigate])

  const { mutateAsync: verifyPhone } = useMutation({
    mutationKey: ['crisisVerifyPhone'],
    mutationFn: async (data: postCrisisStreamAssignProviderPhoneRequest) => {
      return await reactQueryRequest('postCrisisStreamAssignProviderPhone', data)
    },
    onError: (e: string) => {
      onCancelVerify()
      toast.error(e)
    },
    onSuccess: (data) => {
      if (data?.stream) {
        queryClient.setQueryData(streamQueryKey, data)
      }
    },
  })

  const { mutateAsync: validatePhone } = useMutation({
    mutationKey: ['crisisValidatePhone'],
    mutationFn: async (values: CrisisVisitVerifyPhoneValues) => {
      try {
        const { valid } = await reactQueryRequest('postValidatePhone', { phone: values.phoneNumber })
        return valid
      } catch (error) {
        return false
      }
    },
  })

  useEffect(() => {
    if (!readOnly && shouldVerifyPhone && !Modals.list.some((m) => m.type === 'phoneConfirmation')) {
      Modals.open('phoneConfirmation', {
        phoneNumber,
        onVerifyPhone: async (values: CrisisVisitVerifyPhoneValues) => {
          try {
            await verifyPhone({
              providerId,
              phoneNumber: values.phoneNumber,
              eventStreamId,
            })
          } catch (error) {
            console.log(error)
          }
        },
        onValidatePhone: validatePhone,
        onCancel: onCancelVerify,
        onCustomClose: onCancelVerify,
      })
    }
  }, [
    eventStreamId,
    onCancelVerify,
    pendingVisitId,
    providerId,
    phoneNumber,
    shouldVerifyPhone,
    verifyPhone,
    readOnly,
    validatePhone,
  ])

  const { mutate: callLanguageLine } = useMutation({
    mutationKey: ['crisisCallLanguageLine'],
    mutationFn: async () => {
      return await reactQueryRequest('postCrisisCallParticipants', { eventStreamId, participantRole: 'translator' })
    },
    onError: (e: string) => {
      toast.error(e)
    },
    onSuccess: (data) => {
      if (data?.stream) {
        queryClient.setQueryData(streamQueryKey, data)
      }
    },
  })

  const { mutateAsync: requeueVisit } = useMutation({
    mutationKey: ['crisisRequeueVisit', eventStreamId],
    mutationFn: async () => {
      return await reactQueryRequest('postCrisisStreamRequeueVisit', {
        eventStreamId,
        sentBackBy: 'provider',
        sentBackById: provider?.providerId,
        sentBackByName: provider?.fullName,
      })
    },
    onError: (e: string) => {
      toast.error(e)
    },
    onSuccess: (data) => {
      if (data?.stream) {
        queryClient.setQueryData(streamQueryKey, data)
      }
    },
  })

  const isCrisisLanguageLineConnected = useMemo(
    () => !!callParticipants?.connected?.some((i) => i.role === 'translator'),
    [callParticipants?.connected],
  )

  const isProviderConnected = crisisStreamData?.stream?.providerConnected

  const isMemberAnonymous = ext?.privateData.member?.status === MEMBER_STATUSES.eligible

  return {
    claimVisit: claimVisitMutation,
    stream,
    isCrisis,
    shouldVerifyPhone,
    isProviderConnected,
    isCrisisStreamFetching,
    callLanguageLine,
    isCrisisLanguageLineConnected,
    isMemberAnonymous,
    requeueVisit,
  }
}

export const useSelectCrisisStream = (visit?: PendingVisit) => {
  return useCrisisStream({ readOnly: true, visitOverride: visit })
}
